import { useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChatContextType, ChatResponseType, isEverythingContext } from '@trinity/chat';
import { useLocation } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient';
import { useActiveApplicationIdentifier } from '@trinity/chat-components/containers/UserApplications';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import {
  ActiveChatSessionContext,
  ChatHintsLoadingContext,
  ChatInitialResponseContext,
  ChatLandingCardsVisibleContext,
  ChatLandingCardsContext,
  ChatLandingCardsPromptsContext,
  ChatLandingCardsLoadingContext,
  ChatLandingCardsUseNewExperienceContext,
  ChatSessionItemsContext,
} from './context';
import type { LandingPrompt } from './interfaces';

type Props = {
  children: React.ReactNode;
};

export const LandingCardsContainer = (props: Props) => {
  const brandIdentifier = useActiveBrandIdentifier();
  const applicationIdentifier = useActiveApplicationIdentifier();
  // This is the initial visibility from the parent ChatStateContainer (chat state), which is then overridden by the local provider below.
  const landingCardsVisibleCtx = useContext(ChatLandingCardsVisibleContext);
  const chat = useContext(ActiveChatSessionContext);
  const items = useContext(ChatSessionItemsContext);
  const location = useLocation();
  const apiClient = useChatApiClient();
  const initialResponse = useContext(ChatInitialResponseContext);
  const hintsLoading = useContext(ChatHintsLoadingContext);

  const showLandingCards = useMemo(() => {
    if (items.length) {
      return false;
    }

    // When the chat is in the "Everything" (Global) context OR the "bi-global"
    // agent context (which is how the home page registers itself), show landing
    // cards regardless of the current page pathname so that prompts are always
    // visible on the home/global surface.
    if (
      chat.context?.type === ChatContextType.Global ||
      (chat.context != null && isEverythingContext(chat.context))
    ) {
      return true;
    }

    // Don't show landing cards when the chat is embedded inside a scoped
    // surface: workspaces, projects, segments, or the segmentation builder
    // (SegmentProfile's Synthetic Audience tab lives at /segmentation/segment/:id).
    if (
      location.pathname.includes('/workspaces') ||
      location.pathname.includes('/projects') ||
      location.pathname.includes('/project') ||
      location.pathname.includes('/segments') ||
      location.pathname.includes('/segmentation')
    ) {
      return false;
    }

    // Don't show landing cards if chat context type is Document
    if (chat.context?.type === ChatContextType.Document) {
      return false;
    }

    return !!landingCardsVisibleCtx;
  }, [landingCardsVisibleCtx, location.pathname, chat.context, items.length]);

  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const query = useQuery({
    queryKey: ['landing-cards', { brandIdentifier, applicationIdentifier, showLandingCards }],
    enabled: !!brandIdentifier && !!applicationIdentifier && showLandingCards,
    queryFn: async () => {
      if (!brandIdentifier || !applicationIdentifier) {
        return {
          cards: [],
          prompts: [],
          useNewExperience: true,
        };
      }
      const response = await apiClient.chat.getChatLandingCards({
        brandIdentifier: brandIdentifier,
        applicationIdentifier: applicationIdentifier,
      });
      return {
        cards: response.cards,
        prompts: response.prompts,
        useNewExperience: response.useNewExperience,
      };
    },
    placeholderData: {
      cards: [],
      prompts: [],
      useNewExperience: true,
    },
  });

  const hasLandingContent = (query.data?.cards?.length ?? 0) > 0 || (query.data?.prompts?.length ?? 0) > 0;

  // When the landing-cards API returns no prompts (e.g. the
  // application_landing_page_prompts table is empty), fall back to the starter
  // hints provided by getChatInitialMessage so the user always sees prompt
  // suggestions on the home / Everything surface.
  const hintFallbackPrompts = useMemo((): LandingPrompt[] => {
    if (!initialResponse || initialResponse.type !== ChatResponseType.InitialGreeting) {
      return [];
    }
    return initialResponse.data.hints.map(hint => ({
      name: hint.displayValue,
      description: '',
      promptIdentifier: hint.metadata?.promptIdentifier ?? null,
    }));
  }, [initialResponse]);

  const effectivePrompts =
    (query.data?.prompts?.length ?? 0) > 0 ? (query.data?.prompts ?? []) : hintFallbackPrompts;

  const hasEffectiveContent =
    hasLandingContent || hintFallbackPrompts.length > 0;

  return (
    <ChatLandingCardsLoadingContext.Provider value={query.isLoading || (hintsLoading ?? false)}>
      <ChatLandingCardsContext.Provider value={query.data?.cards ?? []}>
        <ChatLandingCardsVisibleContext.Provider value={showLandingCards && hasEffectiveContent}>
          <ChatLandingCardsPromptsContext.Provider value={effectivePrompts}>
            <ChatLandingCardsUseNewExperienceContext.Provider value={query.data?.useNewExperience ?? true}>
              {props.children}
            </ChatLandingCardsUseNewExperienceContext.Provider>
          </ChatLandingCardsPromptsContext.Provider>
        </ChatLandingCardsVisibleContext.Provider>
      </ChatLandingCardsContext.Provider>
    </ChatLandingCardsLoadingContext.Provider>
  );
};
